// src/components/dnl/EditPointDialog.tsx
/**
 * Edit Point Dialog Component
 * 
 * Modal dialog for editing custom points on the double number line.
 * Supports:
 * - Editing label
 * - Changing style (normal/highlighted/question)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { X, HelpCircle } from 'lucide-react';
import {
  updateCustomTick,
} from '../../store/slices/dnlToolsSlice';
import type { DNLCustomTick } from '../../types/dnl';

// =============================================================================
// TYPES
// =============================================================================

export interface EditPointDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The tick to edit */
  tick: DNLCustomTick | null;
  /** Dark mode */
  isDark?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const EditPointDialog: React.FC<EditPointDialogProps> = ({
  isOpen,
  onClose,
  tick,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [label, setLabel] = useState<string>('');
  const [showAsQuestion, setShowAsQuestion] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Reset form when dialog opens or tick changes
  useEffect(() => {
    if (isOpen && tick) {
      setLabel(tick.label || '');
      setShowAsQuestion(tick.style === 'question');
      setError('');
      
      // Focus the label input after a short delay
      setTimeout(() => {
        labelInputRef.current?.focus();
        labelInputRef.current?.select();
      }, 100);
    }
  }, [isOpen, tick]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tick) return;

    const tickLabel = showAsQuestion ? '?' : (label || undefined);
    const style = showAsQuestion ? 'question' : 'normal';

    dispatch(updateCustomTick({
      id: tick.id,
      updates: {
        label: tickLabel,
        style,
      },
    }));

    onClose();
  }, [label, showAsQuestion, tick, dispatch, onClose]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen || !tick) return null;

  // Styles
  const overlayClass = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
  const dialogClass = `rounded-lg shadow-xl max-w-md w-full mx-4 ${
    isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
  }`;
  const headerClass = `flex items-center justify-between p-4 border-b ${
    isDark ? 'border-gray-700' : 'border-gray-200'
  }`;
  const inputClass = `w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${
    isDark ? 'text-gray-300' : 'text-gray-700'
  }`;
  const checkboxLabelClass = `flex items-center gap-2 cursor-pointer text-sm ${
    isDark ? 'text-gray-300' : 'text-gray-600'
  }`;
  const buttonPrimaryClass = 'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-2';
  const buttonSecondaryClass = `px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

  // Format value for display
  const displayValue = tick.value.toFixed(4).replace(/\.?0+$/, '');
  const lineLabel = tick.line === 'primary' ? 'Primary' : tick.line === 'secondary' ? 'Secondary' : 'Both';

  return (
    <div 
      className={overlayClass} 
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-point-title"
    >
      <div 
        className={dialogClass} 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={headerClass}>
          <h2 id="edit-point-title" className="text-lg font-semibold">
            Edit Custom Point
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-md transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Point Info */}
          <div className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-sm">
              <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Value: <span className="font-mono font-semibold">{displayValue}</span>
              </div>
              <div className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Line: <span className="font-semibold">{lineLabel}</span>
              </div>
            </div>
          </div>

          {/* Label Input */}
          <div>
            <label htmlFor="point-label" className={labelClass}>
              Label <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>(optional)</span>
            </label>
            <input
              ref={labelInputRef}
              id="point-label"
              type="text"
              value={label}
              onChange={e => {
                setLabel(e.target.value);
                setError('');
              }}
              className={inputClass}
              placeholder="Custom label (e.g., 125g)"
              disabled={showAsQuestion}
            />
            {error && (
              <p className="mt-1 text-sm text-red-500">{error}</p>
            )}
          </div>

          {/* Show as Question Checkbox */}
          <div>
            <label className={checkboxLabelClass}>
              <input
                type="checkbox"
                checked={showAsQuestion}
                onChange={e => setShowAsQuestion(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <HelpCircle size={16} className="text-amber-500" />
              Show as "?" (unknown value)
            </label>
          </div>

          {/* Actions */}
          <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={onClose}
              className={buttonSecondaryClass}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={buttonPrimaryClass}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPointDialog;

